import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "fontSizeSlider", "fontSizeLabel", "fontSizeLabels",
    "lineSpacingSlider", "lineSpacingLabel", "lineSpacingLabels",
    "saveButton", "saveStatus"
  ]
  static values = {
    aiOptionsId: String,
    fontSizeOptions: Array
  }

  lineSpacingOptions = ['compact', 'normal', 'relaxed'];
  lineSpacingLabels = ['Compact', 'Normal', 'Relaxed'];

  connect() {
    this.editorContent = document.querySelector('.editor-content');
    this.pendingChanges = {};
    this.hasUnsavedChanges = false;
    this.lineSpacingValueIndex = this.serverLineSpacingIndex();
    this.syncLineSpacingControls(this.currentLineSpacingIndex());
    this._formatChanged = event => {
      if (this.pendingChanges.editor_format !== undefined) this.pendingChanges.editor_format = event.detail.value;
    };
    document.addEventListener("document-save:format", this._formatChanged);
    this._saveStateChanged = () => this.refreshAcknowledgedSettings();
    document.addEventListener("document-save:state", this._saveStateChanged);
    this._displayChanged = event => {
      if (event.detail?.setting === 'line_spacing') {
        const index = this.lineSpacingOptions.indexOf(event.detail.value);
        if (index < 0) return;
        this.syncLineSpacingControls(index);
        if (this.pendingChanges.line_spacing !== undefined) this.pendingChanges.line_spacing = event.detail.value;
        return;
      }
      if (event.detail?.setting !== 'font_size') return;
      const value = event.detail.value;
      const index = this.fontSizeOptionsValue.findIndex(([, optionValue]) => optionValue === value);
      if (index < 0) return;
      this.fontSizeSliderTargets.forEach(slider => { slider.value = index; });
      this.fontSizeLabelTargets.forEach(label => { label.textContent = this.fontSizeOptionsValue[index][0]; });
      this.fontSizeLabelsTargets.forEach(labels => this.updateSliderLabels(labels, index));
      if (this.pendingChanges.font_size !== undefined) this.pendingChanges.font_size = value;
    };
    document.addEventListener('editor-settings:changed', this._displayChanged);
  }

  disconnect() {
    document.removeEventListener("document-save:format", this._formatChanged);
    document.removeEventListener("document-save:state", this._saveStateChanged);
    document.removeEventListener('editor-settings:changed', this._displayChanged);
  }

  refreshAcknowledgedSettings() {
    const delivery = document.getElementById("publishForm")?._documentDelivery;
    if (!delivery || !Object.keys(this.pendingChanges).length) return;
    Object.keys(this.pendingChanges).forEach(setting => {
      const row = delivery.session.resources.get(`setting:${setting}`);
      if (row && !row.pending && !row.error && !row.dirty && row.acknowledged === this.pendingChanges[setting]) delete this.pendingChanges[setting];
    });
    this.hasUnsavedChanges = Object.keys(this.pendingChanges).length > 0;
    if (!this.hasUnsavedChanges && this.hasSaveStatusTarget) {
      this.saveStatusTarget.textContent = 'Saved!';
      this.saveStatusTarget.classList.remove('text-danger');
      this.saveStatusTarget.classList.add('text-success');
    }
  }

  slideFontSize(event) {
    const index = parseInt(event.currentTarget.value);
    const [label, value] = this.fontSizeOptionsValue[index];

    // Update label
    if (this.hasFontSizeLabelTarget) {
      this.fontSizeLabelTarget.textContent = label;
    }

    // Update active slider label
    this.updateSliderLabels(this.fontSizeLabelsTarget, index);

    // Apply visual change immediately
    this.applySetting('font_size', value);

    // Track pending change
    this.pendingChanges.font_size = value;
    this.markUnsaved();
  }

  slideLineSpacing(event) {
    const rawIndex = event.currentTarget.value;
    const index = Number(rawIndex);
    if (rawIndex === '' || String(index) !== String(rawIndex) || !Number.isInteger(index) || index < 0 || index >= this.lineSpacingOptions.length) {
      this.syncLineSpacingControls(this.currentLineSpacingIndex());
      return;
    }
    const value = this.lineSpacingOptions[index];
    // Apply visual change immediately
    this.applySetting('line_spacing', value);
    this.syncLineSpacingControls(index);

    // Track pending change
    this.pendingChanges.line_spacing = value;
    this.markUnsaved();
  }

  currentLineSpacingIndex() {
    if (!this.editorContent) return this.lineSpacingValueIndex;
    const index = this.lineSpacingOptions.findIndex(value => this.editorContent?.classList.contains(`line-spacing-${value}`));
    return index < 0 ? 1 : index;
  }

  serverLineSpacingIndex() {
    const index = Number(this.lineSpacingSliderTargets[0]?.value);
    return Number.isInteger(index) && index >= 0 && index < this.lineSpacingOptions.length ? index : 1;
  }

  syncLineSpacingControls(index) {
    const label = this.lineSpacingLabels[index];
    const value = this.lineSpacingOptions[index];
    if (!label || !value) return;
    this.lineSpacingValueIndex = index;
    this.lineSpacingSliderTargets.forEach(slider => {
      slider.value = String(index);
      slider.setAttribute('aria-valuetext', label + (value === 'normal' ? ' (default)' : ''));
    });
    this.lineSpacingLabelTargets.forEach(target => { target.textContent = label; });
    this.lineSpacingLabelsTargets.forEach(container => this.updateSliderLabels(container, index));
  }

  updateSliderLabels(container, activeIndex) {
    if (!container) return;
    const labels = container.querySelectorAll('span');
    labels.forEach((label, i) => {
      if (i === activeIndex) {
        label.classList.add('active');
      } else {
        label.classList.remove('active');
      }
    });
  }

  selectOption(event) {
    const button = event.currentTarget;
    const value = button.dataset.value;
    const btnGroup = button.closest('.editor-setting-btn-group');
    const setting = btnGroup.dataset.setting;

    // Update active state in button group
    btnGroup.querySelectorAll('.editor-setting-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    button.classList.add('active');

    // Apply visual change immediately
    this.applySetting(setting, value);

    // Track pending change
    this.pendingChanges[setting] = value;
    this.markUnsaved();
  }

  selectEditorFormat(event) {
    const button = event.currentTarget;
    const value = button.dataset.value;
    const btnGroup = button.closest('.editor-setting-btn-group');

    // Update active state in button group
    btnGroup.querySelectorAll('.editor-setting-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    button.classList.add('active');

    // Update the left-column class immediately
    const leftColumn = document.querySelector('.left-column');
    if (leftColumn) {
      leftColumn.classList.remove('editor-format-default', 'editor-format-blog', 'editor-format-print');
      leftColumn.classList.add(`editor-format-${value}`);
    }

    // Update the top menu dropdown to stay in sync
    const formatMenuDesc = document.getElementById('formatMenuDesc');
    if (formatMenuDesc) {
      formatMenuDesc.classList.remove('format_default', 'format_blog', 'format_print');
      formatMenuDesc.classList.add(`format_${value}`);
    }
    const menuFormatText = document.getElementById('menu_format');
    if (menuFormatText) {
      menuFormatText.textContent = value === 'blog' ? 'Blog' : 'Document';
    }
    // Update selected state on format buttons in dropdown
    document.querySelectorAll('.format-button').forEach(btn => {
      btn.classList.remove('selected');
    });
    const selectedBtn = document.getElementById(`format_${value}_button`);
    if (selectedBtn) {
      selectedBtn.classList.add('selected');
    }

    // Track pending change
    this.pendingChanges.editor_format = value;
    this.markUnsaved();
  }

  trackChange(event) {
    // For high contrast checkbox
    const checked = event.currentTarget.checked;
    this.applySetting('high_contrast', checked);
    this.pendingChanges.high_contrast = checked.toString();
    this.markUnsaved();
  }

  markUnsaved() {
    const delivery = document.getElementById("publishForm")?._documentDelivery;
    if (delivery) Object.entries(this.pendingChanges).forEach(([setting, value]) => delivery.setSetting(setting, value));
    this.hasUnsavedChanges = true;
    if (this.hasSaveStatusTarget) {
      this.saveStatusTarget.textContent = '';
      this.saveStatusTarget.classList.remove('text-success', 'text-danger');
    }
  }

  async saveAllSettings() {
    if (!this.hasUnsavedChanges && Object.keys(this.pendingChanges).length === 0) {
      return;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    // Disable save button while saving
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = true;
      this.saveButtonTarget.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Saving...';
    }

    const savingChanges = { ...this.pendingChanges };
    try {
      const delivery = document.getElementById("publishForm")?._documentDelivery;
      if (delivery) {
        const results = await Promise.all(Object.entries(savingChanges).map(([setting, value]) => delivery.session.save(delivery.setSetting(setting, value))));
        if (!results.every(Boolean)) throw new Error("Settings request failed");
      } else {
      const promises = [];

      // Save ai_options settings (font_size, line_spacing, font_family, high_contrast)
      const aiOptionsSettings = ['font_size', 'line_spacing', 'font_family', 'high_contrast'];
      for (const setting of aiOptionsSettings) {
        if (savingChanges[setting] !== undefined) {
          promises.push(
            fetch('/ai_options/update_editor_settings', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
                'Accept': 'application/json'
              },
              body: JSON.stringify({ setting, value: savingChanges[setting] })
            })
          );
        }
      }

      // Save editor_format separately via post_options
      if (savingChanges.editor_format !== undefined) {
        promises.push(
          fetch('/post_options/update_editor_format', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken,
              'Accept': 'application/json'
            },
            body: JSON.stringify({ editor_format: savingChanges.editor_format })
          })
        );
      }

      const responses = await Promise.all(promises);
      if (responses.some(response => !response.ok)) throw new Error('Settings request failed');
      }

      // Clear pending changes
      Object.keys(savingChanges).forEach(setting => {
        if (this.pendingChanges[setting] === savingChanges[setting]) delete this.pendingChanges[setting];
      });
      this.hasUnsavedChanges = Object.keys(this.pendingChanges).length > 0;

      // Show success
      if (this.hasSaveStatusTarget) {
        this.saveStatusTarget.textContent = this.hasUnsavedChanges ? 'Unsaved changes' : 'Saved!';
        this.saveStatusTarget.classList.remove('text-danger');
        this.saveStatusTarget.classList.add('text-success');
      }

      // Reset button
      if (this.hasSaveButtonTarget) {
        this.saveButtonTarget.disabled = false;
        this.saveButtonTarget.innerHTML = '<i class="fas fa-check mr-2"></i>Save Settings';
      }

    } catch (error) {
      console.error('Error saving settings:', error);

      if (this.hasSaveStatusTarget) {
        this.saveStatusTarget.textContent = 'Error saving';
        this.saveStatusTarget.classList.add('text-danger');
      }

      if (this.hasSaveButtonTarget) {
        this.saveButtonTarget.disabled = false;
        this.saveButtonTarget.innerHTML = '<i class="fas fa-check mr-2"></i>Save Settings';
      }
    }
  }

  applySetting(setting, value) {
    if (setting === 'line_spacing' && !this.lineSpacingOptions.includes(value)) return;
    if (!this.editorContent) {
      this.editorContent = document.querySelector('.editor-content');
    }

    if (!this.editorContent) return;

    switch (setting) {
      case 'font_size':
        this.editorContent.classList.remove('font-size-xs', 'font-size-small', 'font-size-large', 'font-size-xl');
        if (value !== 'normal') {
          this.editorContent.classList.add(`font-size-${value}`);
        }
        break;
      case 'line_spacing':
        Array.from(this.editorContent.classList).filter(name => name.startsWith('line-spacing-')).forEach(name => this.editorContent.classList.remove(name));
        if (value !== 'normal') {
          this.editorContent.classList.add(`line-spacing-${value}`);
        }
        break;
      case 'font_family':
        this.editorContent.classList.remove('font-serif');
        if (value === 'serif') {
          this.editorContent.classList.add('font-serif');
        }
        // Also remove competing font classes from post-form-wrapper
        // These are added by the existing font dropdown implementation
        const postFormWrapper = document.querySelector('.post-form-wrapper');
        if (postFormWrapper) {
          postFormWrapper.classList.remove('font_family_serif', 'font_family_sans-serif', 'font_family_sans_serif');
        }
        break;
      case 'high_contrast':
        if (value === true || value === 'true') {
          this.editorContent.classList.add('high-contrast');
        } else {
          this.editorContent.classList.remove('high-contrast');
        }
        break;
    }

    // Dispatch event for other components that might need to respond
    document.dispatchEvent(new CustomEvent('editor-settings:changed', {
      detail: { setting, value }
    }));
  }
}
